import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import axiosConn from "config/axiosConn";
import { useSortableRows, SortTh, sortCaption } from "./useSortableRows";
import "./ActivityReports.css";

// Reports show data from 5 June 2026 onward (campaign start).
const CAMPAIGN_START = "2026-06-05";

// ─── §5.1 Indian number formatting ───────────────────────────────────────────
function inr(n) {
  n = Math.round(+n || 0);
  const neg = n < 0;
  let s = String(Math.abs(n));
  if (s.length <= 3) return (neg ? "-" : "") + s;
  let head = s.slice(0, -3), tail = s.slice(-3), parts = [];
  while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2); }
  if (head) parts.unshift(head);
  return (neg ? "-" : "") + parts.join(",") + "," + tail;
}

// State/UT and district breakdowns are shown A→Z (the API returns them ranked by
// participants). Sorting once at load keeps the table, PDF and Excel in the same order.
const byLabel = (rows) =>
  [...(rows || [])].sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), "en", { sensitivity: "base" }));

function sortReportRows(data) {
  if (!data?.reports) return data;
  const reports = {};
  Object.entries(data.reports).forEach(([k, r]) => { reports[k] = r ? { ...r, rows: byLabel(r.rows) } : r; });
  return { ...data, reports };
}

function prettyDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [22, 163, 74];
}

// jsPDF's built-in (non-embedded) fonts only cover Latin-1, so any other script
// (Hindi/Tamil/…) renders as mojibake in the PDF. Canonical English labels are all
// printable-ASCII; non-ASCII labels are untranslated regional free-text.
const pdfSafe = (s) => /^[\x20-\x7E]+$/.test(String(s == null ? "" : s));

// ─── PDF export of the active report (summary + full state/district table) ────
// The API names the state breakdown column "State/UT"; reports say "States/UTs/ABs".
const bucketLabel = (c) => (c === "State/UT" ? "States/UTs/ABs" : c);

function downloadReportPDF({ meta, report, bucketColumn, scopeLabel, period, recordCount }) {
  if (!report) return;
  const { summary, rows, grand } = report;
  const isPlant = !!meta.plantation;
  const accent = hexToRgb(meta.accent);

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 40; // page margin

  // ── Header band ──
  doc.setFillColor(...accent);
  doc.rect(0, 0, pageW, 92, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(`NATIONAL ACTIVITY REPORT · ${meta.tab.toUpperCase()}`, M, 30);
  doc.setFontSize(17);
  doc.text(meta.title, M, 54, { maxWidth: pageW - M * 2 });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(
    `Scope: ${scopeLabel}   |   Period: ${prettyDate(period.from)} onwards   |   Records: ${inr(recordCount)} events`,
    M,
    78
  );

  // ── Summary tiles row ──
  const tiles = meta.key === "pledge"
    ? [
        ["SCHOOLS PARTICIPATED", summary.schools],
        ["TOTAL PLEDGES TAKEN", summary.participants],
        ["STUDENTS", summary.students],
        ["TEACHERS", summary.teachers],
      ]
    : [
        ["EVENTS", summary.events],
        ["SCHOOLS PARTICIPATED", summary.schools],
        ["STUDENTS", summary.students],
        ["TEACHERS", summary.teachers],
        ["TOTAL PARTICIPANTS", summary.participants],
      ];
  if (isPlant) tiles.push(["TREES PLANTED", summary.trees]);

  let y = 112;
  const gap = 8;
  const tileW = (pageW - M * 2 - gap * (tiles.length - 1)) / tiles.length;
  const tileH = 56;
  tiles.forEach((t, i) => {
    const x = M + i * (tileW + gap);
    doc.setFillColor(247, 250, 252);
    doc.setDrawColor(...accent);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, tileW, tileH, 6, 6, "FD");
    doc.setTextColor(...accent);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(inr(t[1]), x + tileW / 2, y + 24, { align: "center" });
    doc.setTextColor(90, 100, 115);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text(t[0], x + tileW / 2, y + 40, { align: "center", maxWidth: tileW - 6 });
  });

  // ── Breakdown table ──
  const head = [
    ["#", bucketColumn.toUpperCase(), "SCHOOLS", "STUDENTS", "TEACHERS", "TOTAL PARTICIPANTS"],
  ];
  if (isPlant) head[0].push("TREES PLANTED");

  const body = rows.map((r, i) => {
    const row = [i + 1, r.label, inr(r.schools), inr(r.students), inr(r.teachers), inr(r.participants)];
    if (isPlant) row.push(inr(r.trees));
    return row;
  });

  const foot = [["", "GRAND TOTAL", inr(grand.schools), inr(grand.students), inr(grand.teachers), inr(grand.participants)]];
  if (isPlant) foot[0].push(inr(grand.trees));

  const numCols = isPlant ? [2, 3, 4, 5, 6] : [2, 3, 4, 5];
  const colStyles = { 0: { halign: "center", cellWidth: 26 } };
  numCols.forEach((c) => (colStyles[c] = { halign: "right" }));

  doc.autoTable({
    head,
    body,
    foot,
    startY: y + tileH + 18,
    margin: { left: M, right: M },
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 4, lineColor: [230, 234, 240], lineWidth: 0.4, textColor: [40, 50, 65] },
    headStyles: { fillColor: accent, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5, halign: "left" },
    footStyles: { fillColor: [11, 18, 32], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: colStyles,
    didDrawPage: (d) => {
      const ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(7.5);
      doc.setTextColor(140, 150, 165);
      doc.setFont("helvetica", "normal");
      doc.text(
        "Eco Clubs For Mission LiFE · Department of School Education & Literacy, Ministry of Education, Government of India",
        M,
        ph - 18
      );
      doc.text(`Page ${d.pageNumber}`, pageW - M, ph - 18, { align: "right" });
    },
  });

  // Other report: append a Theme → Sub-theme breakdown table (English) with a total.
  if (meta.key === "other" && Array.isArray(report.themes) && report.themes.length) {
    const tHead = [["THEME / SUB-THEME", "SCHOOLS", "STUDENTS", "TEACHERS", "PARTICIPANTS", "EVENTS"]];
    const tBody = [];
    const themeRowIdx = new Set();
    // Students/teachers/participants/events sum cleanly across themes (each record
    // lands in exactly one theme), so the TOTAL row adds them up. SCHOOLS does NOT:
    // a school active in several themes is distinct-counted under each, so summing
    // per-theme school counts overstates it — the TOTAL uses the report's distinct
    // school count (grand.schools), which matches the "Schools Participated" tile.
    const tot = { students: 0, teachers: 0, participants: 0, events: 0 };
    // The backend already canonicalises to English and buckets untranslatable
    // regional text into "Other"/"Others"; pdfSafe is a last-resort guard so a stray
    // non-Latin-1 label can never render as mojibake in the PDF.
    report.themes.forEach((t) => {
      themeRowIdx.add(tBody.length);
      tBody.push([pdfSafe(t.theme) ? t.theme : "Other", inr(t.schools), inr(t.students), inr(t.teachers), inr(t.participants), inr(t.events)]);
      tot.students += t.students || 0; tot.teachers += t.teachers || 0;
      tot.participants += t.participants || 0; tot.events += t.events || 0;
      (t.subThemes || []).forEach((s) => {
        const label = pdfSafe(s.subTheme) ? s.subTheme : "Others";
        tBody.push([`     -  ${label}`, inr(s.schools), inr(s.students), inr(s.teachers), inr(s.participants), inr(s.events)]);
      });
    });
    const tFoot = [["TOTAL (ALL THEMES)", inr(grand.schools), inr(tot.students), inr(tot.teachers), inr(tot.participants), inr(tot.events)]];
    const startY2 = (doc.lastAutoTable?.finalY || 0) + 28;
    doc.setFontSize(11);
    doc.setTextColor(...accent);
    doc.setFont("helvetica", "bold");
    doc.text("Theme & Sub-theme Breakdown", M, startY2 - 8);
    doc.autoTable({
      head: tHead,
      body: tBody,
      foot: tFoot,
      startY: startY2,
      margin: { left: M, right: M },
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 4, lineColor: [230, 234, 240], lineWidth: 0.4, textColor: [40, 50, 65] },
      headStyles: { fillColor: accent, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5, halign: "left" },
      footStyles: { fillColor: [11, 18, 32], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
      didParseCell: (d) => {
        if (d.section === "body" && themeRowIdx.has(d.row.index)) {
          d.cell.styles.fontStyle = "bold";
          d.cell.styles.fillColor = [236, 240, 245];
        }
      },
    });
    const noteY = (doc.lastAutoTable?.finalY || startY2) + 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 130, 145);
    doc.text(
      "Schools are counted distinctly within each theme, so per-theme school counts overlap and do not sum to the total. The total shows distinct schools participated.",
      M, noteY, { maxWidth: pageW - M * 2 }
    );
  }

  const safe = (s) => String(s).replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
  doc.save(`Eco_Clubs_${safe(meta.tab)}_${safe(scopeLabel)}_${period.from}.pdf`);
}

// ─── Report meta (title, colour, banner, plantation flag) ────────────────────
// Order = tab order. Plantation is the DEFAULT tab.
const REPORTS = [
  {
    key: "plantation",
    tab: "Plantation",
    title: "Plantation Report, Ek Ped Maa Ke Naam 3.0",
    subtitle: "Every tree-planting activity across the campaign · schools, participants & trees",
    icon: "🌳",
    accent: "#16a34a",
    accentSoft: "#f0fdf4",
    plantation: true,
    banner: null,
  },
  {
    key: "other",
    tab: "Other Themes",
    title: "Other Themes Report",
    subtitle: "All remaining theme activities",
    icon: "🌿",
    accent: "#d97706",
    accentSoft: "#fffbeb",
    plantation: false,
    banner:
      "This report excludes Pledge and Plantation (Ek Ped Maa Ke Naam 3.0) activities, which are covered in their own reports.",
  },
  // Pledge report — district-wise (state login) / state-wise (national), with the
  // same table + PDF download. Sourced from the Environment Day pledge submissions.
  {
    key: "pledge",
    tab: "Pledge",
    title: "Environment Day Pledge Report",
    subtitle: "Schools that have taken the Mission LiFE pledge · participants",
    icon: "🤝",
    accent: "#0ea5e9",
    accentSoft: "#f0f9ff",
    plantation: false,
    banner:
      "Pledge counts come from the Environment Day pledge submissions (one row per school). Defaults to the 5-June campaign window; use “Since 15 May (All)” to match the Environment Day dashboard.",
  },
];
const DEFAULT_TAB = "plantation";

// ─── Summary tile (colored-band "boxdiv" style) ───────────────────────────────
// `tone` picks the palette: head text colour, TOTAL band, and tinted value band.
function Tile({ icon, value, label, tone = "green" }) {
  // First word stays muted ("NUMBER OF"), the rest is coloured & emphasised.
  const words = String(label).trim().split(" ");
  const lead = words.length > 1 ? words.shift() : "";
  const rest = words.join(" ");
  return (
    <div className={`ar-box ar-box-${tone}`}>
      <div className="ar-box-top">
        <p className="ar-box-cap">
          {lead && <span>{lead} </span>}
          <span className="ar-box-cap-strong">{rest}</span>
        </p>
        <span className="ar-box-icon">{icon}</span>
      </div>
      <div className="ar-box-bottom">
        <p className="ar-box-total">TOTAL</p>
        <h2 className="ar-box-value">{inr(value)}</h2>
      </div>
    </div>
  );
}

// ─── Theme → Sub-theme breakdown (Other report) ──────────────────────────────
function ThemeBreakdown({ themes }) {
  const [open, setOpen] = useState(() => new Set());
  if (!Array.isArray(themes) || themes.length === 0) return null;
  // Totals across all themes — students/teachers/participants/events reconcile to
  // the Other report's grand total (each record counts under exactly one theme).
  const tot = themes.reduce(
    (a, t) => ({
      schools: a.schools + (t.schools || 0),
      students: a.students + (t.students || 0),
      teachers: a.teachers + (t.teachers || 0),
      participants: a.participants + (t.participants || 0),
      events: a.events + (t.events || 0),
    }),
    { schools: 0, students: 0, teachers: 0, participants: 0, events: 0 }
  );
  const toggle = (name) =>
    setOpen((prev) => {
      const n = new Set(prev);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  return (
    <div className="ar-table-card" style={{ marginTop: 16 }}>
      <div className="ar-table-card-head">Theme &amp; Sub-theme Breakdown · click a theme to expand</div>
      <div className="ar-table-wrap">
        <table className="ar-table">
          <thead>
            <tr>
              <th className="ar-col-name">Theme / Sub-theme</th>
              <th className="ar-num">Schools</th>
              <th className="ar-num">Students</th>
              <th className="ar-num">Teachers</th>
              <th className="ar-num">Total Participants</th>
              <th className="ar-num">Events</th>
            </tr>
          </thead>
          <tbody>
            {themes.map((t) => {
              const isOpen = open.has(t.theme);
              const hasSubs = (t.subThemes || []).length > 0;
              return (
                <React.Fragment key={t.theme}>
                  <tr
                    className="ar-theme-row"
                    style={{ cursor: hasSubs ? "pointer" : "default", fontWeight: 700 }}
                    onClick={() => hasSubs && toggle(t.theme)}
                  >
                    <td className="ar-label">{hasSubs ? (isOpen ? "▼ " : "▶ ") : "• "}{t.theme}</td>
                    <td className="ar-num">{inr(t.schools)}</td>
                    <td className="ar-num">{inr(t.students)}</td>
                    <td className="ar-num">{inr(t.teachers)}</td>
                    <td className="ar-num ar-strong">{inr(t.participants)}</td>
                    <td className="ar-num">{inr(t.events)}</td>
                  </tr>
                  {isOpen &&
                    (t.subThemes || []).map((s) => (
                      <tr key={t.theme + "::" + s.subTheme} className="ar-subtheme-row" style={{ background: "#fafbfc" }}>
                        <td className="ar-label" style={{ paddingLeft: 28, color: "#475569" }}>↳ {s.subTheme}</td>
                        <td className="ar-num">{inr(s.schools)}</td>
                        <td className="ar-num">{inr(s.students)}</td>
                        <td className="ar-num">{inr(s.teachers)}</td>
                        <td className="ar-num">{inr(s.participants)}</td>
                        <td className="ar-num">{inr(s.events)}</td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="ar-grand">
              <td className="ar-label">TOTAL (all themes)</td>
              <td className="ar-num">{inr(tot.schools)}</td>
              <td className="ar-num">{inr(tot.students)}</td>
              <td className="ar-num">{inr(tot.teachers)}</td>
              <td className="ar-num ar-strong">{inr(tot.participants)}</td>
              <td className="ar-num">{inr(tot.events)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─── One report PANEL (summary + table) ──────────────────────────────────────
function ReportPanel({ meta, report, bucketColumn, scopeLabel, period, recordCount, onRowClick, pledgeMode, onPledgeModeChange }) {
  const isPlant = meta.plantation;

  const { summary, rows: rawRows, grand } = report || {};
  // Click-to-sort. Starts on the server's order (participants desc) so the table
  // looks unchanged until a header is clicked.
  const { sorted: rows, sort, toggle } = useSortableRows(rawRows || []);

  if (!report) {
    return <div className="ar-panel-empty">No data available for this report.</div>;
  }

  return (
    <div
      className="ar-panel"
      role="tabpanel"
      id={`ar-panel-${meta.key}`}
      aria-labelledby={`ar-tab-${meta.key}`}
      style={{ "--accent": meta.accent, "--accent-soft": meta.accentSoft }}
    >
      {/* Meta row */}
      <div className="ar-panel-head">
        <div className="ar-panel-head-left">
          <h2 className="ar-panel-title">{meta.title}</h2>
          <p className="ar-panel-sub">{meta.subtitle}</p>
        </div>
        <span className="ar-chip">{scopeLabel}</span>
      </div>

      <div className="ar-period">
        <span>{`📅 ${prettyDate(period.from)} onwards`}</span>
        <span className="ar-dot">·</span>
        <span>{`${inr(recordCount)} records in scope`}</span>
      </div>

      {/* Pledge window toggle (matches the Environment Day dashboard) */}
      {meta.key === "pledge" && onPledgeModeChange && (
        <div className="ar-pledge-toggle">
          <button
            className={`ar-preset ${pledgeMode === "after5june" ? "on" : ""}`}
            onClick={() => onPledgeModeChange("after5june")}
          >
            From 5 June
          </button>
          <button
            className={`ar-preset ${pledgeMode === "all" ? "on" : ""}`}
            onClick={() => onPledgeModeChange("all")}
          >
            Since 15 May (All)
          </button>
        </div>
      )}

      {/* Banner */}
      {meta.banner && <div className="ar-banner">ℹ️ {meta.banner}</div>}

      {/* KPI tiles — colored-band boxdiv style */}
      <div className="ar-tiles">
        {meta.key === "pledge" ? (
          // Pledge tiles mirror the Environment Day dashboard: School Participated,
          // Pledges Taken (= participants), Students, Teachers.
          <>
            <Tile icon="🏫" value={summary.schools} label="Schools Participated" tone="brown" />
            <Tile icon="🤝" value={summary.participants} label="Total Pledges Taken" tone="olive" />
            <Tile icon="🎓" value={summary.students} label="Total Students" tone="purple" />
            <Tile icon="🧑‍🏫" value={summary.teachers} label="Total Teachers" tone="orange" />
          </>
        ) : (
          <>
            <Tile icon="📋" value={summary.events} label="Total Events" tone="olive" />
            <Tile icon="🏫" value={summary.schools} label="Schools Participated" tone="brown" />
            <Tile icon="🎓" value={summary.students} label="Total Students" tone="purple" />
            <Tile icon="🧑‍🏫" value={summary.teachers} label="Total Teachers" tone="orange" />
            <Tile icon="👥" value={summary.participants} label="Total Participants" tone="blue" />
            {isPlant && <Tile icon="🌱" value={summary.trees} label="Trees Planted" tone="green" />}
          </>
        )}
      </div>

      {/* Breakdown table (full width) */}
      <div className="ar-table-card">
        <div className="ar-table-card-head">
          Breakdown ({bucketColumn}-wise) ·{" "}
          {sortCaption(sort, {
            label: bucketColumn, schools: "Schools", students: "Students",
            teachers: "Teachers", participants: "Total Participants", trees: "Trees Planted",
          })}{" "}
          <span className="ar-sort-hint">· click any column to sort</span>
        </div>
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th className="ar-num ar-col-rank">#</th>
                <SortTh label={bucketColumn} sortKey="label" sort={sort} onSort={toggle} className="ar-col-name" numeric={false} />
                <SortTh label="Schools" sortKey="schools" sort={sort} onSort={toggle} className="ar-num" />
                <SortTh label="Students" sortKey="students" sort={sort} onSort={toggle} className="ar-num" />
                <SortTh label="Teachers" sortKey="teachers" sort={sort} onSort={toggle} className="ar-num" />
                <SortTh label="Total Participants" sortKey="participants" sort={sort} onSort={toggle} className="ar-num" />
                {isPlant && <SortTh label="Trees Planted" sortKey="trees" sort={sort} onSort={toggle} className="ar-num" />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={isPlant ? 7 : 6} className="ar-empty">
                    No activity in this report for the selected period.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={r.label + i}
                    className="ar-row-click"
                    style={{ cursor: "pointer" }}
                    title={`Click to view schools in ${r.label}`}
                    onClick={() => onRowClick && onRowClick(r)}
                  >
                    <td className="ar-num ar-rank">{i + 1}</td>
                    <td className="ar-label ar-label-link">{r.label}</td>
                    <td className="ar-num">{inr(r.schools)}</td>
                    <td className="ar-num">{inr(r.students)}</td>
                    <td className="ar-num">{inr(r.teachers)}</td>
                    <td className="ar-num ar-strong">{inr(r.participants)}</td>
                    {isPlant && <td className="ar-num ar-trees">{inr(r.trees)}</td>}
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="ar-grand">
                  <td className="ar-num"></td>
                  <td className="ar-label">GRAND TOTAL</td>
                  <td className="ar-num">{inr(grand.schools)}</td>
                  <td className="ar-num">{inr(grand.students)}</td>
                  <td className="ar-num">{inr(grand.teachers)}</td>
                  <td className="ar-num ar-strong">{inr(grand.participants)}</td>
                  {isPlant && <td className="ar-num ar-trees">{inr(grand.trees)}</td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Theme → sub-theme breakdown (Other report only) */}
      {meta.key === "other" && <ThemeBreakdown themes={report.themes} />}
    </div>
  );
}

// State-admin (role 4) usernames look like "EC4ML_delhi" — the state often isn't
// on userData.state, so derive it from the username key the same way the
// Environment Day dashboard does. Returns a display name or null.
const STATE_FROM_USERNAME_KEY = {
  andhrapradesh: "Andhra Pradesh", arunachalpradesh: "Arunachal Pradesh", assam: "Assam",
  bihar: "Bihar", chhattisgarh: "Chhattisgarh", goa: "Goa", gujarat: "Gujarat",
  haryana: "Haryana", himachalpradesh: "Himachal Pradesh", "jammu&kashmir": "Jammu & Kashmir",
  jharkhand: "Jharkhand", karnataka: "Karnataka", kerala: "Kerala",
  madhyapradesh: "Madhya Pradesh", maharashtra: "Maharashtra", manipur: "Manipur",
  meghalaya: "Meghalaya", mizoram: "Mizoram", nagaland: "Nagaland", odisha: "Odisha",
  punjab: "Punjab", rajasthan: "Rajasthan", sikkim: "Sikkim", tamilnadu: "Tamil Nadu",
  telangana: "Telangana", tripura: "Tripura", uttarakhand: "Uttarakhand",
  uttarpradesh: "Uttar Pradesh", westbengal: "West Bengal", delhi: "Delhi",
  "andaman&nicobarislands": "Andaman & Nicobar Islands", chandigarh: "Chandigarh",
  damananddiu: "Dadra & Nagar Haveli and Daman & Diu", lakshadweep: "Lakshadweep",
  puducherry: "Puducherry", kvs: "Kendriya Vidyalaya Sanghathan",
  nvs: "Navodaya Vidyalaya Samiti", ladakh: "Ladakh",
};

function resolveAdminState(userData) {
  if (!userData) return null;
  if (userData.state) return userData.state;
  const username = userData.username;
  if (typeof username === "string" && username.includes("_")) {
    const key = username.split("_")[1]?.toLowerCase();
    if (key && STATE_FROM_USERNAME_KEY[key]) return STATE_FROM_USERNAME_KEY[key];
  }
  return null;
}

// Compact numbered pagination list with ellipses, e.g. [1,"…",49,50,51,"…",100].
// `siblings` = how many pages to show on each side of the current page.
function paginationItems(current, total, siblings = 1) {
  const range = (s, e) => Array.from({ length: Math.max(0, e - s + 1) }, (_, i) => s + i);
  const totalShown = siblings * 2 + 5; // first + last + current + 2 dots + 2*siblings
  if (total <= totalShown) return range(1, total);
  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, total);
  const leftDots = left > 2;
  const rightDots = right < total - 1;
  if (!leftDots && rightDots) return [...range(1, 3 + 2 * siblings), "dots-r", total];
  if (leftDots && !rightDots) return [1, "dots-l", ...range(total - (2 + 2 * siblings), total)];
  return [1, "dots-l", ...range(left, right), "dots-r", total];
}

// ─── Inline drill-down panel (state→districts→blocks→schools) ────────────────
// Rendered INLINE on the same page (not a route, not a modal overlay) because the
// portal's translation extension reverts any navigation. Clicking a row sets the
// entry; this fetches and shows the next level in place, with a Back button.
export function DrillModal({ open, reportKey, isPlant, isStateAdmin, adminState, fromDate, toDate, pledgeMode, entry, onClose }) {
  const [stack, setStack] = useState([]);
  const [search, setSearch] = useState("");          // search box value
  const [appliedSearch, setAppliedSearch] = useState(""); // debounced value sent to the server
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const SCHOOLS_PER_PAGE = 50;

  const makeBody = useCallback(
    (extra) => {
      const b = { reportType: reportKey, ...extra };
      if (fromDate) b.fromDate = fromDate;
      if (toDate) b.toDate = toDate;
      if (reportKey === "pledge") b.pledgeMode = pledgeMode;
      return b;
    },
    [reportKey, fromDate, toDate, pledgeMode]
  );

  // Open the schools level (push/replace a frame); the page-fetch effect below loads
  // the actual page from the server. Reset search + page when entering a new level.
  const loadSchools = useCallback((stateName, district, push, block = "") => {
    setSearch(""); setAppliedSearch(""); setPage(1);
    const frame = { kind: "schools", state: stateName, district, block, loading: true, schools: [], total: 0, totals: null };
    setStack((s) => (push ? [...s, frame] : [frame]));
  }, []);

  // Block-wise level of one district: one row per block. The server groups the
  // same school rows the school-wise view lists, so blocks and schools always
  // add up to each other. `__none__` collects schools whose block is not
  // recorded yet (those are fetched from UDISE+ in the background).
  const loadBlocks = useCallback(
    async (stateName, district, push) => {
      setSearch(""); setAppliedSearch(""); setPage(1);
      const frame = { kind: "blocks", state: stateName, district, loading: true, rows: [], schoolsTotal: 0, totals: null, unrecorded: 0 };
      setStack((s) => (push ? [...s, frame] : [frame]));
      try {
        const reqBody = makeBody({ district, groupBy: "block" });
        if (stateName) reqBody.state = stateName;
        const res = await axiosConn("post", "activity-reports/schools", reqBody);
        const d = res?.data?.success ? res.data : null;
        setStack((s) => s.map((f, i) => (i === s.length - 1
          ? { ...f, loading: false, rows: d?.blocks || [], schoolsTotal: d?.total || 0, totals: d?.totals || null, unrecorded: d?.unrecorded || 0 }
          : f)));
      } catch {
        setStack((s) => s.map((f, i) => (i === s.length - 1 ? { ...f, loading: false, rows: [] } : f)));
      }
    },
    [makeBody]
  );

  const loadDistricts = useCallback(
    async (stateName, push) => {
      const frame = { kind: "districts", state: stateName, loading: true, rows: [] };
      setStack((s) => (push ? [...s, frame] : [frame]));
      try {
        const res = await axiosConn("post", "activity-reports/state", makeBody({ state: stateName }));
        const rows = res?.data?.success ? byLabel(res.data.reports?.[reportKey]?.rows) : [];
        setStack((s) => s.map((f, i) => (i === s.length - 1 ? { ...f, loading: false, rows } : f)));
      } catch {
        setStack((s) => s.map((f, i) => (i === s.length - 1 ? { ...f, loading: false, rows: [] } : f)));
      }
    },
    [makeBody, reportKey]
  );

  useEffect(() => {
    if (!open || !entry) {
      setStack([]);
      setSearch(""); setAppliedSearch(""); setPage(1);
      return;
    }
    setSearch(""); setAppliedSearch(""); setPage(1);
    if (isStateAdmin) loadBlocks(adminState, entry.label, false);
    else loadDistricts(entry.label, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry]);

  // Debounce the search box → applied (server) search; reset to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setAppliedSearch(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side page fetch: whenever the schools level / page / search changes, pull
  // ONLY that page (50 rows) from the backend — never the whole list. The frame
  // key (kind · district · block) is a dependency because the School-wise ⇄
  // Block-wise toggle REPLACES the top frame without changing the stack length.
  const topFrame = stack[stack.length - 1];
  const topKey = topFrame ? `${topFrame.kind}|${topFrame.state || ""}|${topFrame.district || ""}|${topFrame.block || ""}` : "";
  useEffect(() => {
    const t = stack[stack.length - 1];
    if (!t || t.kind !== "schools") return;
    let cancelled = false;
    (async () => {
      setStack((s) => s.map((f, i) => (i === s.length - 1 ? { ...f, loading: true } : f)));
      try {
        const reqBody = makeBody({ district: t.district, page, limit: SCHOOLS_PER_PAGE, search: appliedSearch, ...(t.block ? { block: t.block } : {}) });
        if (t.state) reqBody.state = t.state;
        const res = await axiosConn("post", "activity-reports/schools", reqBody);
        if (cancelled) return;
        const d = res?.data?.success ? res.data : null;
        setStack((s) => s.map((f, i) => (i === s.length - 1
          ? { ...f, loading: false, schools: d?.schools || [], total: d?.total || 0, totals: d?.totals || null }
          : f)));
      } catch {
        if (!cancelled) setStack((s) => s.map((f, i) => (i === s.length - 1 ? { ...f, loading: false, schools: [], total: 0 } : f)));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearch, stack.length, topKey]);

  // Click-to-sort for the drill-down tables. Declared BEFORE the early return
  // below so the hook order stays stable across renders.
  const peek = stack.length ? stack[stack.length - 1] : null;
  const districtSort = useSortableRows(peek?.kind === "districts" ? peek.rows || [] : []);
  const blockSort = useSortableRows(peek?.kind === "blocks" ? peek.rows || [] : []);
  // Schools arrive one server page at a time, so this orders the visible page only
  // — the header says as much, and the Excel export carries the complete list.
  const schoolSort = useSortableRows(peek?.kind === "schools" ? peek.schools || [] : []);

  if (!open || stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const goBack = () => setStack((s) => s.slice(0, -1));
  const blockName = (b) => (b === "__none__" ? "Block not recorded" : b);
  // School-wise ⇄ Block-wise inside a district: replace the top frame.
  const swapTop = (kind) => {
    if (top.kind === kind) return;
    setStack((s) => s.slice(0, -1));
    if (kind === "blocks") loadBlocks(top.state, top.district, true);
    else loadSchools(top.state, top.district, true);
  };
  const q = search.trim().toLowerCase();
  const visibleBlocks = top.kind === "blocks" ? (blockSort.sorted || []).filter((b) => !q || b.label.toLowerCase().includes(q)) : [];

  // Schools view: the server already returned just this page + the full total.
  const schoolTotal = top.kind === "schools" ? (top.total || 0) : 0;
  const schoolPages = Math.max(1, Math.ceil(schoolTotal / SCHOOLS_PER_PAGE));
  const safePage = Math.min(page, schoolPages);
  const pageStartIdx = (safePage - 1) * SCHOOLS_PER_PAGE;

  // Excel export. Districts are already fully loaded; for schools we fetch the FULL
  // list (all:true) on demand here — the table itself only ever pulls one page.
  const safeName = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
  const downloadExcel = async () => {
    setDownloading(true);
    try {
      let rows, sheetName, fileName, extraSheet = null;
      if (top.kind === "blocks") {
        rows = (top.rows || []).map((b, i) => {
          const o = { "#": i + 1, Block: b.label, Schools: b.schools, Students: b.students, Teachers: b.teachers, "Total Participants": b.participants };
          if (isPlant) o["Trees Planted"] = b.trees;
          return o;
        });
        // second sheet: every school of the district with its block
        const reqBody = makeBody({ district: top.district, all: true });
        if (top.state) reqBody.state = top.state;
        const res = await axiosConn("post", "activity-reports/schools", reqBody);
        const all = res?.data?.success ? res.data.schools || [] : [];
        extraSheet = ["Schools", all.map((s, i) => {
          const o = { "#": i + 1, Block: s.block || "Block not recorded", School: s.school_name, UDISE: s.udise_id, Students: s.students, Teachers: s.teachers, "Total Participants": s.participants };
          if (isPlant) o["Trees Planted"] = s.trees;
          return o;
        })];
        sheetName = "Blocks";
        fileName = `Eco_Clubs_${safeName(reportKey)}_${safeName(top.state)}_${safeName(top.district)}_blocks.xlsx`;
      } else if (top.kind === "districts") {
        rows = (top.rows || []).map((d, i) => {
          const o = { "#": i + 1, District: d.label, Schools: d.schools, Students: d.students, Teachers: d.teachers, "Total Participants": d.participants };
          if (isPlant) o["Trees Planted"] = d.trees;
          return o;
        });
        sheetName = "Districts";
        fileName = `Eco_Clubs_${safeName(reportKey)}_${safeName(top.state)}_districts.xlsx`;
      } else {
        const reqBody = makeBody({ district: top.district, all: true, search: appliedSearch, ...(top.block ? { block: top.block } : {}) });
        if (top.state) reqBody.state = top.state;
        const res = await axiosConn("post", "activity-reports/schools", reqBody);
        const all = res?.data?.success ? res.data.schools || [] : [];
        rows = all.map((s, i) => {
          const o = { "#": i + 1, School: s.school_name, UDISE: s.udise_id, Block: s.block || "—", Students: s.students, Teachers: s.teachers, "Total Participants": s.participants };
          if (isPlant) o["Trees Planted"] = s.trees;
          return o;
        });
        sheetName = "Schools";
        fileName = `Eco_Clubs_${safeName(reportKey)}_${safeName(top.state)}_${safeName(top.district)}${top.block ? `_${safeName(blockName(top.block))}` : ""}_schools.xlsx`;
      }
      if (!rows.length) return;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName);
      if (extraSheet && extraSheet[1].length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(extraSheet[1]), extraSheet[0]);
      XLSX.writeFile(wb, fileName);
    } catch {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // State-wide export from the DISTRICTS view: one sheet with EVERY school of the
  // state (a District column per row) — no need to drill into each district first.
  // The server returns all districts' schools when the body carries no district.
  const downloadAllSchoolsExcel = async () => {
    setDownloadingAll(true);
    try {
      const reqBody = makeBody({ all: true });
      if (top.state) reqBody.state = top.state;
      const res = await axiosConn("post", "activity-reports/schools", reqBody);
      const all = res?.data?.success ? res.data.schools || [] : [];
      const rows = all.map((s, i) => {
        const o = { "#": i + 1, District: s.district || "—", School: s.school_name, UDISE: s.udise_id, Students: s.students, Teachers: s.teachers, "Total Participants": s.participants };
        if (isPlant) o["Trees Planted"] = s.trees;
        return o;
      });
      if (!rows.length) { alert("No schools to export."); return; }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Schools");
      XLSX.writeFile(wb, `Eco_Clubs_${safeName(reportKey)}_${safeName(top.state)}_all_schools.xlsx`);
    } catch {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloadingAll(false);
    }
  };

  // Page-button styling (numbered pagination).
  const pgBtn = (active, disabled) => ({
    minWidth: 36, height: 34, padding: "0 10px", borderRadius: 8,
    border: active ? "1px solid #16a34a" : "1px solid #d8dee6",
    background: active ? "#16a34a" : "#fff",
    color: active ? "#fff" : disabled ? "#cbd5e1" : "#0f172a",
    fontWeight: active ? 700 : 500,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 13.5,
    transition: "background .15s, border-color .15s",
  });

  return (
    <div className="ar-table-card" style={{ marginTop: 14 }}>
      <div
        className="ar-table-card-head"
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <button
          className="ar-refresh"
          style={{ padding: "5px 12px" }}
          onClick={() => (stack.length > 1 ? goBack() : onClose())}
        >
          ← Back{stack.length > 1 ? "" : " to report"}
        </button>
        <span style={{ fontWeight: 700 }}>
          {top.kind === "schools"
            ? `🏫 Schools · ${top.block ? `${blockName(top.block)} · ` : ""}${top.district}${top.state && !isStateAdmin ? ` (${top.state})` : ""}`
            : top.kind === "blocks"
              ? `🧭 Blocks · ${top.district}${top.state && !isStateAdmin ? ` (${top.state})` : ""}`
              : `🏛️ Districts · ${top.state}`}
        </span>
        {(top.kind === "blocks" || (top.kind === "schools" && !top.block)) && (
          <div className="ar-presets" style={{ padding: 3 }} role="tablist" aria-label="View">
            <button type="button" className={`ar-preset${top.kind === "schools" ? " on" : ""}`} style={{ padding: "6px 13px" }} onClick={() => swapTop("schools")}>School-wise</button>
            <button type="button" className={`ar-preset${top.kind === "blocks" ? " on" : ""}`} style={{ padding: "6px 13px" }} onClick={() => swapTop("blocks")}>Block-wise</button>
          </div>
        )}
        <button
          className="ar-download"
          style={{ marginLeft: "auto" }}
          onClick={downloadExcel}
          disabled={downloading || (top.kind === "schools" ? schoolTotal === 0 : (top.rows || []).length === 0)}
          title={top.kind === "schools" ? "Download ALL schools as Excel" : top.kind === "blocks" ? "Download the block summary + every school of this district with its block" : "Download these districts as Excel"}
        >
          {downloading ? "Preparing…" : "⬇️ Download Excel"}
        </button>
        {top.kind === "districts" && (
          <button
            className="ar-download"
            onClick={downloadAllSchoolsExcel}
            disabled={downloadingAll || (top.rows || []).length === 0}
            title="Download EVERY school of this state (all districts, school-wise) as one Excel"
          >
            {downloadingAll ? "Preparing…" : "🏫 All Schools Excel"}
          </button>
        )}
      </div>

      {(top.kind === "schools" || top.kind === "blocks") && (
        <div className="ar-modal-search" style={{ padding: "10px 14px 0" }}>
          <input placeholder={top.kind === "blocks" ? "🔍 Search block…" : "🔍 Search school, UDISE or block…"} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}
      {top.kind === "blocks" && !top.loading && top.unrecorded > 0 && (
        <div className="ar-sort-note" style={{ margin: "10px 14px 0", background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
          {`${inr(top.unrecorded)} school${top.unrecorded === 1 ? "" : "s"} have no block recorded yet — their blocks are being fetched from UDISE+ in the background and will appear on the next visit.`}
        </div>
      )}

      <div className="ar-table-wrap">
          {top.loading ? (
            <div className="ar-modal-loading"><div className="ar-spinner" /> Loading…</div>
          ) : top.kind === "districts" ? (
            <table className="ar-table">
              <thead>
                <tr>
                  <th className="ar-num">#</th>
                  <SortTh label="District" sortKey="label" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-col-name" numeric={false} />
                  <SortTh label="Schools" sortKey="schools" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-num" />
                  <SortTh label="Students" sortKey="students" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-num" />
                  <SortTh label="Teachers" sortKey="teachers" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-num" />
                  <SortTh label="Participants" sortKey="participants" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-num" />
                  {isPlant && <SortTh label="Trees" sortKey="trees" sort={districtSort.sort} onSort={districtSort.toggle} className="ar-num" />}
                </tr>
              </thead>
              <tbody>
                {top.rows.length === 0 ? (
                  <tr><td colSpan={isPlant ? 7 : 6} className="ar-empty">No districts in scope.</td></tr>
                ) : (
                  districtSort.sorted.map((d, i) => (
                    <tr key={d.label + i} style={{ cursor: "pointer" }} title={`View blocks in ${d.label}`}
                        onClick={() => loadBlocks(top.state, d.label, true)}>
                      <td className="ar-num">{i + 1}</td>
                      <td className="ar-label ar-label-link">{d.label}</td>
                      <td className="ar-num">{inr(d.schools)}</td>
                      <td className="ar-num">{inr(d.students)}</td>
                      <td className="ar-num">{inr(d.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(d.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(d.trees)}</td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : top.kind === "blocks" ? (
            <table className="ar-table">
              <thead>
                <tr>
                  <th className="ar-num">#</th>
                  <SortTh label="Block" sortKey="label" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-col-name" numeric={false} />
                  <SortTh label="Schools" sortKey="schools" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-num" />
                  <SortTh label="Students" sortKey="students" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-num" />
                  <SortTh label="Teachers" sortKey="teachers" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-num" />
                  <SortTh label="Participants" sortKey="participants" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-num" />
                  {isPlant && <SortTh label="Trees" sortKey="trees" sort={blockSort.sort} onSort={blockSort.toggle} className="ar-num" />}
                </tr>
              </thead>
              <tbody>
                {visibleBlocks.length === 0 ? (
                  <tr><td colSpan={isPlant ? 7 : 6} className="ar-empty">{q ? "No block matches the search." : "No schools in this district for the selected period."}</td></tr>
                ) : (
                  visibleBlocks.map((b, i) => (
                    <tr key={b.key + i} style={{ cursor: "pointer" }} title={`View schools in ${b.label}`}
                        onClick={() => loadSchools(top.state, top.district, true, b.key)}>
                      <td className="ar-num">{i + 1}</td>
                      <td className="ar-label ar-label-link">{b.label}</td>
                      <td className="ar-num">{inr(b.schools)}</td>
                      <td className="ar-num">{inr(b.students)}</td>
                      <td className="ar-num">{inr(b.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(b.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(b.trees)}</td>}
                    </tr>
                  ))
                )}
              </tbody>
              {(top.rows || []).length > 0 && top.totals && (
                <tfoot>
                  <tr className="ar-grand">
                    <td className="ar-num"></td>
                    <td className="ar-label">{`TOTAL · ${inr((top.rows || []).length)} blocks`}</td>
                    <td className="ar-num">{inr(top.schoolsTotal)}</td>
                    <td className="ar-num">{inr(top.totals.students)}</td>
                    <td className="ar-num">{inr(top.totals.teachers)}</td>
                    <td className="ar-num ar-strong">{inr(top.totals.participants)}</td>
                    {isPlant && <td className="ar-num">{inr(top.totals.trees)}</td>}
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <>
              {schoolTotal > SCHOOLS_PER_PAGE && (
                <div className="ar-sort-note">
                  Click a column to sort — this list is paged, so sorting orders the {SCHOOLS_PER_PAGE} rows
                  on this page. Use “Download Excel” for the full {inr(schoolTotal)}-school list.
                </div>
              )}
              <table className="ar-table">
                <thead>
                  <tr>
                    <th className="ar-num">#</th>
                    <SortTh label="School" sortKey="school_name" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-col-name" numeric={false} title="Sort this page by School" />
                    <SortTh label="UDISE" sortKey="udise_id" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-num" title="Sort this page by UDISE" />
                    <SortTh label="Block" sortKey="block" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-label" numeric={false} title="Sort this page by Block" />
                    <SortTh label="Students" sortKey="students" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-num" title="Sort this page by Students" />
                    <SortTh label="Teachers" sortKey="teachers" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-num" title="Sort this page by Teachers" />
                    <SortTh label="Participants" sortKey="participants" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-num" title="Sort this page by Participants" />
                    {isPlant && <SortTh label="Trees" sortKey="trees" sort={schoolSort.sort} onSort={schoolSort.toggle} className="ar-num" title="Sort this page by Trees" />}
                  </tr>
                </thead>
                <tbody>
                  {schoolTotal === 0 ? (
                    <tr><td colSpan={isPlant ? 8 : 7} className="ar-empty">No schools found.</td></tr>
                  ) : (
                    (schoolSort.sorted || []).map((s, i) => (
                      <tr key={(s.udise_id || s.school_name) + (pageStartIdx + i)}>
                        <td className="ar-num">{pageStartIdx + i + 1}</td>
                        <td className="ar-label">{s.school_name || "—"}</td>
                        <td className="ar-num">{s.udise_id || "—"}</td>
                        <td className="ar-label" style={{ fontWeight: 500, color: "#475569" }}>{s.block || "—"}</td>
                        <td className="ar-num">{inr(s.students)}</td>
                        <td className="ar-num">{inr(s.teachers)}</td>
                        <td className="ar-num ar-strong">{inr(s.participants)}</td>
                        {isPlant && <td className="ar-num">{inr(s.trees)}</td>}
                      </tr>
                    ))
                  )}
                </tbody>
                {schoolTotal > 0 && top.totals && (
                  <tfoot>
                    <tr className="ar-grand">
                      <td className="ar-num"></td>
                      <td className="ar-label">{`TOTAL · ${inr(schoolTotal)} schools`}</td>
                      <td className="ar-num"></td>
                      <td className="ar-label"></td>
                      <td className="ar-num">{inr(top.totals.students)}</td>
                      <td className="ar-num">{inr(top.totals.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(top.totals.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(top.totals.trees)}</td>}
                    </tr>
                  </tfoot>
                )}
              </table>
              {schoolTotal > SCHOOLS_PER_PAGE && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", padding: "14px" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                    <button style={pgBtn(false, safePage <= 1)} disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Prev</button>
                    {paginationItems(safePage, schoolPages).map((it, idx) =>
                      typeof it === "number" ? (
                        <button
                          key={idx}
                          style={pgBtn(it === safePage, false)}
                          onClick={() => setPage(it)}
                          aria-current={it === safePage ? "page" : undefined}
                        >
                          {it}
                        </button>
                      ) : (
                        <span key={idx} style={{ minWidth: 22, textAlign: "center", color: "#94a3b8" }}>…</span>
                      )
                    )}
                    <button style={pgBtn(false, safePage >= schoolPages)} disabled={safePage >= schoolPages} onClick={() => setPage((p) => Math.min(schoolPages, p + 1))}>Next ›</button>
                  </div>
                  <span style={{ fontSize: 12.5, color: "#64748b" }}>
                    {`Showing ${inr(pageStartIdx + 1)}–${inr(Math.min(pageStartIdx + SCHOOLS_PER_PAGE, schoolTotal))} of ${inr(schoolTotal)} schools · Page ${safePage} of ${schoolPages}`}
                  </span>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
export default function ActivityReports({ embedded = false }) {
  const userData = useSelector((s) => s.auth.user);
  const isStateAdmin = userData?.role === 4;
  const adminState = resolveAdminState(userData);
  const scopeLabel = isStateAdmin && adminState ? adminState : "National";

  const [fromDate, setFromDate] = useState(CAMPAIGN_START);
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePreset, setActivePreset] = useState("5june");
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  // Inline drill-down: null = show the report; a clicked row = show its schools/
  // districts inline (no navigation — see openSchools for why).
  const [drillEntry, setDrillEntry] = useState(null);
  // Pledge tab window: "after5june" (default, like the rest of the screen) or
  // "all" (since 15 May — matches the Environment Day dashboard).
  const [pledgeMode, setPledgeMode] = useState("after5june");
  const [downloadingAllSchools, setDownloadingAllSchools] = useState(false);

  const tabRefs = useRef({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = isStateAdmin ? "activity-reports/state" : "activity-reports/national";
      const body = {};
      if (fromDate) body.fromDate = fromDate;
      if (toDate) body.toDate = toDate;
      // The /state route has no auth middleware, so req.user is empty server-side.
      // We must send the resolved state in the body (derived from username when
      // userData.state is absent, e.g. "EC4ML_delhi" → Delhi).
      if (isStateAdmin && adminState) body.state = adminState;
      body.pledgeMode = pledgeMode; // only affects the Pledge report server-side
      const res = await axiosConn("post", url, body);
      if (res?.data?.success) setData(sortReportRows(res.data));
      else setError("Failed to load activity reports.");
    } catch (err) {
      console.error("Activity reports fetch error:", err);
      setError(err?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, isStateAdmin, adminState, pledgeMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Open the drill-down INLINE (just a React state change — NO route change). Every
  // form of navigation (SPA, hard window.location, even a new tab) gets reverted by
  // the page-translation browser extension on this portal: it re-loads/translates the
  // page on any URL change and lands the user back on the dashboard. Rendering the
  // schools inline avoids navigation entirely, so the extension can't hijack it.
  const openSchools = useCallback((row) => setDrillEntry(row), []);

  // Download the current main report's breakdown (State/UT-wise or District-wise) as
  // Excel. For "Other Themes" a second sheet carries the theme → sub-theme breakdown.
  const downloadReportExcel = () => {
    const rep = data?.reports?.[activeTab];
    if (!rep) return;
    const isPlant = !!activeMeta.plantation;
    const col = bucketLabel(data?.bucketColumn) || "States/UTs/ABs";
    const safe = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
    const body = (rep.rows || []).map((r, i) => {
      const o = { "#": i + 1, [col]: r.label, Schools: r.schools, Students: r.students, Teachers: r.teachers, "Total Participants": r.participants };
      if (isPlant) o["Trees Planted"] = r.trees;
      return o;
    });
    const g = rep.grand || {};
    const total = { "#": "", [col]: "GRAND TOTAL", Schools: g.schools, Students: g.students, Teachers: g.teachers, "Total Participants": g.participants };
    if (isPlant) total["Trees Planted"] = g.trees;
    body.push(total);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(body), col === "District" ? "Districts" : "States-UTs-ABs");
    // Other Themes → add the theme/sub-theme breakdown as a second sheet.
    if (activeTab === "other" && Array.isArray(rep.themes) && rep.themes.length) {
      const t = [];
      rep.themes.forEach((th) => {
        t.push({ "Theme / Sub-theme": th.theme, Schools: th.schools, Students: th.students, Teachers: th.teachers, Participants: th.participants, Events: th.events });
        (th.subThemes || []).forEach((s) => t.push({ "Theme / Sub-theme": "    - " + s.subTheme, Schools: s.schools, Students: s.students, Teachers: s.teachers, Participants: s.participants, Events: s.events }));
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(t), "Themes");
    }
    XLSX.writeFile(wb, `Eco_Clubs_${safe(activeTab)}_${safe(data?.scope || scopeLabel)}_${col === "District" ? "districts" : "states"}.xlsx`);
  };

  // State admin: download EVERY school of the state (all districts, one sheet with a
  // District column) straight from the main report — no district click needed. The
  // schools endpoint returns the whole state when the body carries no district.
  const downloadAllSchoolsExcel = async () => {
    setDownloadingAllSchools(true);
    try {
      const isPlant = !!activeMeta.plantation;
      const body = { reportType: activeTab, all: true };
      if (fromDate) body.fromDate = fromDate;
      if (toDate) body.toDate = toDate;
      if (activeTab === "pledge") body.pledgeMode = pledgeMode;
      const stateForBody = data?.scope || adminState;
      if (stateForBody) body.state = stateForBody;
      const res = await axiosConn("post", "activity-reports/schools", body);
      const all = res?.data?.success ? res.data.schools || [] : [];
      const safe = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");
      const rows = all.map((s, i) => {
        const o = { "#": i + 1, District: s.district || "—", School: s.school_name, UDISE: s.udise_id, Students: s.students, Teachers: s.teachers, "Total Participants": s.participants };
        if (isPlant) o["Trees Planted"] = s.trees;
        return o;
      });
      if (!rows.length) { alert("No schools to export."); return; }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Schools");
      XLSX.writeFile(wb, `Eco_Clubs_${safe(activeTab)}_${safe(stateForBody || scopeLabel)}_all_schools.xlsx`);
    } catch {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloadingAllSchools(false);
    }
  };

  const applyPreset = (preset) => {
    setActivePreset(preset);
    if (preset === "5june") { setFromDate(CAMPAIGN_START); setToDate(""); }
    // "custom" leaves the inputs as-is
  };

  const activeMeta = REPORTS.find((r) => r.key === activeTab) || REPORTS[0];

  // Keyboard arrow navigation across tabs (a11y baseline for a govt portal).
  const onTabKeyDown = (e) => {
    const idx = REPORTS.findIndex((r) => r.key === activeTab);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next =
        e.key === "ArrowRight"
          ? (idx + 1) % REPORTS.length
          : (idx - 1 + REPORTS.length) % REPORTS.length;
      const nk = REPORTS[next].key;
      setActiveTab(nk);
      tabRefs.current[nk]?.focus();
    }
  };

  return (
    <div className={`ar-page ${embedded ? "ar-embedded" : ""}`}>
      {/* ── Top bar ── */}
      <div className="ar-topbar">
        <div className="ar-topbar-left">
          <div className="ar-title-row">
            <span className="ar-title-icon">📊</span>
            <div className="ar-title-block">
              <div className="ar-eyebrow">
                <span className="ar-dot-live" /> Eco Clubs · Mission LiFE
              </div>
              <h1 className="ar-h1">Activity Reports</h1>
            </div>
          </div>
          <p className="ar-h1-sub">
            <span className="ar-scope-pill">{`${scopeLabel} ${isStateAdmin ? "(District-wise)" : "(States/UTs/ABs-wise)"}`}</span>
            <span className="ar-dot">·</span>
            <span>{`from ${prettyDate(CAMPAIGN_START)}`}</span>
          </p>
        </div>
        <div className="ar-topbar-right">
          <div className="ar-presets">
            <button className={`ar-preset ${activePreset === "5june" ? "on" : ""}`} onClick={() => applyPreset("5june")}>From 5 June</button>
            <button className={`ar-preset ${activePreset === "custom" ? "on" : ""}`} onClick={() => applyPreset("custom")}>Custom</button>
          </div>
          {/* Date-range filter is always visible — admins pick any window from 5 June
              onward. "From 5 June" resets it; editing a date switches to Custom. */}
          <div className="ar-custom-range">
            <label>From
              <input
                type="date"
                min={CAMPAIGN_START}
                value={fromDate}
                onChange={(e) => { setActivePreset("custom"); setFromDate(e.target.value); }}
              />
            </label>
            <label>To
              <input
                type="date"
                min={CAMPAIGN_START}
                value={toDate}
                onChange={(e) => { setActivePreset("custom"); setToDate(e.target.value); }}
              />
            </label>
          </div>
          <div className="ar-action-row">
            {/* PDF/Excel here download the MAIN breakdown (State/UT-wise or
                District-wise). While drilled into a state/district, they're hidden so
                the only visible "Download Excel" is the drill's — which exports exactly
                what's on screen (its districts, or all schools of the district). */}
            {!drillEntry && (
              <>
                <button
                  className="ar-download"
                  onClick={() =>
                    downloadReportPDF({
                      meta: activeMeta,
                      report: data?.reports?.[activeTab],
                      bucketColumn: bucketLabel(data?.bucketColumn),
                      scopeLabel: data?.scope || scopeLabel,
                      period:
                        activeTab === "pledge" && data?.reports?.pledge?.fromDate
                          ? { ...data.period, from: data.reports.pledge.fromDate }
                          : data?.period,
                      recordCount: data?.reports?.[activeTab]?.summary?.events ?? data?.recordCount,
                    })
                  }
                  disabled={loading || !data?.reports?.[activeTab]}
                  title="Download the current report as a PDF"
                >
                  ⬇️ Download PDF
                </button>
                <button
                  className="ar-download"
                  onClick={downloadReportExcel}
                  disabled={loading || !data?.reports?.[activeTab]?.rows?.length}
                  title={`Download the ${isStateAdmin ? "district-wise" : "state-wise"} breakdown as Excel`}
                >
                  ⬇️ Download Excel
                </button>
                {isStateAdmin && (
                  <button
                    className="ar-download"
                    onClick={downloadAllSchoolsExcel}
                    disabled={loading || downloadingAllSchools || !data?.reports?.[activeTab]?.rows?.length}
                    title="Download EVERY school of the state (all districts, school-wise) as one Excel"
                  >
                    {downloadingAllSchools ? "Preparing…" : "🏫 All Schools Excel"}
                  </button>
                )}
              </>
            )}
            <button className="ar-refresh" onClick={fetchData} disabled={loading}>
              {loading ? <span className="ar-mini-spin" /> : "🔄"} Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Tab bar + panel ── */}
      {!loading && !error && data && (
        <div className="ar-tabwrap">
          <div className="ar-tabbar" role="tablist" aria-label="Activity report tabs" onKeyDown={onTabKeyDown}>
            {REPORTS.map((meta) => {
              const rep = data.reports?.[meta.key];
              const evts = rep?.summary?.events ?? 0;
              const on = activeTab === meta.key;
              return (
                <button
                  key={meta.key}
                  id={`ar-tab-${meta.key}`}
                  ref={(el) => (tabRefs.current[meta.key] = el)}
                  className={`ar-tab ${on ? "on" : ""}`}
                  style={{ "--accent": meta.accent }}
                  role="tab"
                  aria-selected={on}
                  aria-controls={`ar-panel-${meta.key}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => { setActiveTab(meta.key); setDrillEntry(null); }}
                >
                  <span className="ar-tab-icon">{meta.icon}</span>
                  <span className="ar-tab-label">{meta.tab}</span>
                  <span className="ar-tab-badge">{inr(evts)}</span>
                </button>
              );
            })}
          </div>

          <div className="ar-tabpanel-shell">
            {drillEntry ? (
              <DrillModal
                open
                entry={drillEntry}
                reportKey={activeTab}
                isPlant={activeMeta.plantation}
                isStateAdmin={isStateAdmin}
                adminState={data?.scope || adminState}
                fromDate={fromDate}
                toDate={toDate}
                pledgeMode={pledgeMode}
                onClose={() => setDrillEntry(null)}
              />
            ) : (
              <ReportPanel
                key={activeTab}
                meta={activeMeta}
                report={data.reports?.[activeTab]}
                bucketColumn={bucketLabel(data.bucketColumn)}
                scopeLabel={data.scope}
                period={
                  activeTab === "pledge" && data.reports?.pledge?.fromDate
                    ? { ...data.period, from: data.reports.pledge.fromDate }
                    : data.period
                }
                recordCount={data.reports?.[activeTab]?.summary?.events ?? data.recordCount}
                onRowClick={openSchools}
                pledgeMode={pledgeMode}
                onPledgeModeChange={setPledgeMode}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Loading / error ── */}
      {loading && (
        <div className="ar-loading">
          <div className="ar-spinner" />
          <span>Loading activity reports…</span>
        </div>
      )}

      {!loading && error && (
        <div className="ar-error">
          <div style={{ fontSize: 30 }}>⚠️</div>
          <div className="ar-error-title">Failed to load reports</div>
          <div className="ar-error-msg">{error}</div>
          <button className="ar-refresh" onClick={fetchData}>Retry</button>
        </div>
      )}

    </div>
  );
}
